import { memo, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { AnimatedCategoryIcon } from '@/components/help-center/AnimatedCategoryIcon';
import type { CmsCategory } from '@/lib/cms-types';
import { cn } from '@/lib/utils';

/** Shell navy — topo mais escuro; o glow colorido do hover faz o contraste. */
function CategoryDarkCardShell({
  children,
  className,
  innerClassName,
}: {
  children: ReactNode;
  className?: string;
  innerClassName?: string;
}) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border border-blue-900/60',
        'bg-[#030023] shadow-lg shadow-black/25 ring-1 ring-sky-500/10',
        className
      )}
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#06083a] via-[#04042e] to-[#030023]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#000b6a]/40 via-transparent to-[#01012e]"
        aria-hidden
      />
      <div className={cn('relative z-10', innerClassName)}>{children}</div>
    </div>
  );
}

type PremiumCategoryCardProps = {
  category: CmsCategory;
  articleCount: number;
  iconClassName?: string;
  badgeClassName?: string;
  /** Cor do contorno da badge (hex / css). */
  badgeBorderColor?: string;
  hoverBorderClassName?: string;
  hoverShadowClassName?: string;
  onClick?: () => void;
  /** Acções do painel (ex.: Editar / Excluir) — canto superior esquerdo. */
  actions?: ReactNode;
  className?: string;
};

type CardMood =
  | 'friendly'
  | 'account'
  | 'security'
  | 'legendary'
  | 'chat'
  | 'store'
  | 'technical'
  | 'mobile'
  | 'live'
  | 'default';

const CARD_MOOD: Record<string, CardMood> = {
  'primeiros-passos': 'friendly',
  account: 'account',
  safety: 'security',
  powers: 'legendary',
  chats: 'chat',
  store: 'store',
  troubleshooting: 'technical',
  mobile: 'mobile',
  'xat-live': 'live',
};

function Sparkle({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 12 12" fill="currentColor" className={className} aria-hidden>
      <path d="M6 0c.4 2.8 1.6 4.6 6 6-4.4 1.4-5.6 3.2-6 6-.4-2.8-1.6-4.6-6-6 4.4-1.4 5.6-3.2 6-6Z" />
    </svg>
  );
}

/** Detalhes premium extremamente discretos — só se notam ao olhar com atenção. */
function SubtleEffects({ mood, reducedMotion }: { mood: CardMood; reducedMotion: boolean }) {
  if (reducedMotion) return null;

  if (mood === 'friendly') {
    return (
      <>
        {[
          { className: 'left-[14%] top-[22%] h-1.5 w-1.5', delay: 0.4 },
          { className: 'right-[16%] top-[30%] h-1 w-1', delay: 1.8 },
          { className: 'right-[28%] bottom-[26%] h-1 w-1', delay: 3.2 },
        ].map((s, i) => (
          <motion.span
            key={i}
            className={cn('pointer-events-none absolute text-sky-300/40', s.className)}
            animate={{ opacity: [0.12, 0.45, 0.12], scale: [0.85, 1.1, 0.85] }}
            transition={{ duration: 4.5 + i, delay: s.delay, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Sparkle className="h-full w-full" />
          </motion.span>
        ))}
      </>
    );
  }

  if (mood === 'account') {
    return (
      <>
        {[0, 1].map((i) => (
          <motion.span
            key={i}
            className="pointer-events-none absolute rounded-full bg-sky-400/[0.06] blur-md"
            style={{
              width: 48 + i * 28,
              height: 48 + i * 28,
              right: 8 + i * 20,
              top: 36 + i * 40,
            }}
            animate={{ opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 5.5 + i, delay: i * 1.2, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </>
    );
  }

  if (mood === 'security') {
    return (
      <svg
        viewBox="0 0 200 240"
        className="pointer-events-none absolute inset-0 h-full w-full text-sky-400/[0.08]"
        fill="none"
        aria-hidden
      >
        <path d="M24 48h40 M136 48h40 M24 120h28 M148 120h28 M24 192h40 M136 192h40" stroke="currentColor" strokeWidth="1" />
        <path d="M48 24v40 M48 136v40 M152 24v40 M152 136v40" stroke="currentColor" strokeWidth="1" />
      </svg>
    );
  }

  if (mood === 'legendary') {
    return (
      <>
        <motion.span
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(0,11,106,0.35),transparent_55%)]"
          animate={{ opacity: [0.45, 0.85, 0.45] }}
          transition={{ duration: 3.8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <svg
          viewBox="0 0 260 220"
          className="pointer-events-none absolute inset-0 h-full w-full text-sky-400/20"
          fill="none"
          aria-hidden
        >
          <motion.path
            d="M8 58h12l4-7 5 14 5-7h12"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinecap="round"
            animate={{ opacity: [0.15, 0.55, 0.15] }}
            transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 3.2 }}
          />
          <motion.path
            d="M252 158h-12l-4-7-5 14-5-7h-12"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinecap="round"
            animate={{ opacity: [0.15, 0.5, 0.15] }}
            transition={{ duration: 1.4, delay: 1.6, repeat: Infinity, repeatDelay: 3.2 }}
          />
        </svg>
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="pointer-events-none absolute h-0.5 w-0.5 rounded-full bg-cyan-300/70 shadow-[0_0_6px_rgba(34,211,238,0.55)]"
            style={{ left: `${20 + i * 28}%`, top: `${34 + (i % 2) * 30}%` }}
            animate={{ opacity: [0, 0.7, 0], y: [2, -4, 2] }}
            transition={{
              duration: 1.8,
              delay: i * 0.9,
              repeat: Infinity,
              repeatDelay: 2.4,
            }}
          />
        ))}
      </>
    );
  }

  if (mood === 'chat') {
    return (
      <>
        {[0, 1, 2, 3].map((i) => (
          <motion.span
            key={i}
            className="pointer-events-none absolute h-0.5 w-0.5 rounded-full bg-sky-300/40"
            style={{ left: `${18 + i * 18}%`, top: `${28 + (i % 3) * 22}%` }}
            animate={{ opacity: [0.1, 0.4, 0.1], y: [0, -3, 0] }}
            transition={{ duration: 4 + i * 0.5, delay: i * 0.7, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </>
    );
  }

  if (mood === 'store') {
    return (
      <>
        {[0, 1].map((i) => (
          <motion.span
            key={i}
            className="pointer-events-none absolute text-amber-200/30"
            style={{ right: `${14 + i * 20}%`, top: `${20 + i * 36}%` }}
            animate={{ opacity: [0.1, 0.4, 0.1], scale: [0.8, 1.1, 0.8] }}
            transition={{ duration: 3.6, delay: 1.2 + i * 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Sparkle className="h-2 w-2" />
          </motion.span>
        ))}
      </>
    );
  }

  if (mood === 'technical') {
    return (
      <svg
        viewBox="0 0 200 240"
        className="pointer-events-none absolute inset-0 h-full w-full text-sky-400/[0.07]"
        fill="none"
        aria-hidden
      >
        <path d="M20 60h60 M120 60h60 M20 120h40 M140 120h40 M20 180h60 M120 180h60" stroke="currentColor" strokeWidth="1" />
      </svg>
    );
  }

  if (mood === 'mobile') {
    return (
      <svg
        viewBox="0 0 100 100"
        className="pointer-events-none absolute -right-1 top-2 h-20 w-20 text-cyan-300/20"
        fill="none"
        aria-hidden
      >
        {[16, 26, 36].map((r, i) => (
          <motion.path
            key={r}
            d={`M${50 - r} 50a${r} ${r} 0 0 1 ${r * 2} 0`}
            stroke="currentColor"
            strokeWidth="1"
            animate={{ opacity: [0.08, 0.35, 0.08] }}
            transition={{ duration: 3.2, delay: i * 0.6, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </svg>
    );
  }

  if (mood === 'live') {
    return (
      <motion.span
        className="pointer-events-none absolute left-1/2 top-[42%] h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full border border-fuchsia-300/15"
        animate={{ scale: [0.9, 1.15], opacity: [0.3, 0] }}
        transition={{ duration: 3.4, repeat: Infinity, ease: 'easeOut' }}
      />
    );
  }

  return null;
}

function PremiumCategoryCardBase({
  category,
  articleCount,
  iconClassName,
  badgeClassName,
  badgeBorderColor,
  hoverBorderClassName,
  hoverShadowClassName,
  onClick,
  actions,
  className,
}: PremiumCategoryCardProps) {
  const reducedMotion = useReducedMotion() ?? false;
  const mood = CARD_MOOD[category.id] ?? 'default';
  const badgeText =
    category.badge != null && String(category.badge).trim() !== ''
      ? String(category.badge).trim()
      : null;

  const shell = (
    <CategoryDarkCardShell
      className={cn(
        'h-full min-h-[268px] transition-[box-shadow,border-color,ring-color] duration-300',
        hoverBorderClassName,
        hoverShadowClassName
      )}
      innerClassName="relative flex h-full min-h-[268px] flex-col items-center px-6 py-6 text-center"
    >
      {/* Hover: azul mais claro na mesma direção do degradê do card (diagonal BR) */}
      <span
        className={cn(
          'pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300',
          'bg-gradient-to-br from-[#000b6a] via-[#000b6a]/35 to-transparent',
          'group-hover:opacity-100'
        )}
        aria-hidden
      />

      <div className="pointer-events-none absolute inset-0 opacity-70">
        <SubtleEffects mood={mood} reducedMotion={reducedMotion} />
      </div>

      {actions ? <div className="absolute left-3 top-3 z-30 flex items-center gap-1">{actions}</div> : null}

      {badgeText ? (
        <span className="absolute right-3 top-3 z-20">
          <span
            className={cn(
              'rounded-full border border-solid px-2 py-0.5 font-poppins text-[10px] font-semibold leading-none backdrop-blur-sm',
              badgeClassName
            )}
            style={badgeBorderColor ? { borderColor: badgeBorderColor } : undefined}
          >
            {badgeText}
          </span>
        </span>
      ) : null}

      <div className="relative z-10">
        <AnimatedCategoryIcon
          categoryId={category.id}
          icon={category.icon}
          iconClassName={iconClassName}
        />
      </div>

      <h3 className="relative z-10 mb-2 font-poppins text-lg font-bold text-white transition-colors duration-300 group-hover:text-sky-100">
        {category.name}
      </h3>
      <p className="relative z-10 mb-5 line-clamp-3 font-poppins text-sm leading-relaxed text-blue-100/75 transition-colors duration-300 group-hover:text-blue-100/90">
        {category.description || 'Sem descrição.'}
      </p>

      {/* Separador horizontal = Powerpédia (duas linhas + cristal no meio) */}
      <div className="relative z-10 mb-4 flex h-2 w-full shrink-0 items-center" aria-hidden>
        <span
          className="block h-px min-w-0 flex-1"
          style={{
            background: 'linear-gradient(to right, transparent, rgba(56,189,248,0.4))',
          }}
        />
        <span className="mx-3 h-2 w-2 shrink-0 rotate-45 rounded-[3px] bg-sky-400/70 shadow-[0_0_12px_rgba(56,189,248,0.65)]" />
        <span
          className="block h-px min-w-0 flex-1"
          style={{
            background: 'linear-gradient(to right, rgba(56,189,248,0.4), transparent)',
          }}
        />
      </div>

      <div className="relative z-10 mt-auto flex w-full items-center justify-between">
        <span className="font-poppins text-xs font-medium text-blue-100">
          {articleCount === 1 ? '1 artigo' : `${articleCount} artigos`}
        </span>
        <ChevronRight
          className="h-5 w-5 shrink-0 text-blue-100 transition-transform duration-300 group-hover:translate-x-1"
          strokeWidth={2.5}
          aria-hidden
        />
      </div>
    </CategoryDarkCardShell>
  );

  const wrapClass = cn(
    'group relative block w-full text-left transition-transform duration-300 ease-out',
    !reducedMotion && 'hover:-translate-y-2',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#030023]',
    className
  );

  // Com `actions` (painel) não usamos <button> externo — evita botão aninhado.
  if (actions || !onClick) {
    return (
      <div
        className={wrapClass}
        onClick={onClick}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        {shell}
      </div>
    );
  }

  return (
    <button type="button" onClick={onClick} className={wrapClass}>
      {shell}
    </button>
  );
}

export const PremiumCategoryCard = memo(PremiumCategoryCardBase);
